/**
 * Board filters: who, which labels, what text, what order.
 *
 * Pure, so the rules the team cares most about are pinned by
 * filters.test.ts rather than by whoever last touched the screen:
 *
 *   - Clicking a person shows only their cards. Clicking someone else
 *     switches straight to them. Clicking the active person clears it.
 *   - Compare mode (Shift-click or the toggle) adds and removes people
 *     instead of switching.
 *   - All of it lives in the URL, so /tasks/<board>?person=reid opens on
 *     Reid's cards. ?archived=1 is the archive. A text search also finds
 *     archived cards, so a closed card can be opened without that view.
 */

import { isArchived } from "./archive";
import type { Card, Label, Person, Priority } from "./types";

export type SortKey = "manual" | "updated" | "due" | "priority" | "created";
export const SORT_KEYS: SortKey[] = ["manual", "updated", "due", "priority", "created"];

export interface FilterState {
  /** Employee ids, in the order they were picked (last = most recent). */
  people: string[];
  labels: string[];
  q: string;
  sort: SortKey;
  compare: boolean;
  /** The archive: cards taken off the board, still on the row. */
  archived: boolean;
}

export const EMPTY_FILTERS: FilterState = {
  people: [], labels: [], q: "", sort: "manual", compare: false, archived: false,
};

export function isFiltering(f: FilterState): boolean {
  return f.people.length > 0 || f.labels.length > 0 || f.q.trim() !== "" || f.archived;
}

// ── Person clicks ──────────────────────────────────────────────────────────

/**
 * What a click on a person's pill does.
 *
 * `additive` is true for a Shift-click; compare mode makes every click
 * additive.
 */
export function clickPerson(f: FilterState, id: string, additive = false): FilterState {
  if (additive || f.compare) {
    const people = f.people.includes(id) ? f.people.filter(p => p !== id) : [...f.people, id];
    return { ...f, people };
  }
  const onlyThem = f.people.length === 1 && f.people[0] === id;
  return { ...f, people: onlyThem ? [] : [id] };
}

/** Leaving compare mode keeps only the person picked most recently. */
export function setCompare(f: FilterState, on: boolean): FilterState {
  if (on) return { ...f, compare: true };
  return { ...f, compare: false, people: f.people.length > 1 ? [f.people[f.people.length - 1]] : f.people };
}

export function clickLabel(f: FilterState, id: string): FilterState {
  return { ...f, labels: f.labels.includes(id) ? f.labels.filter(l => l !== id) : [...f.labels, id] };
}

export function clearFilters(f: FilterState): FilterState {
  return { ...EMPTY_FILTERS, sort: f.sort };
}

// ── URL slugs ──────────────────────────────────────────────────────────────

function slugify(s: string): string {
  return s.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

/**
 * A short, stable URL name per person: "reid". Two people who share a first
 * name both become "first-last". If even that collides, the id prefix
 * settles it.
 */
export function personSlugs(people: Person[]): Map<string, string> {
  const first = (p: Person) => slugify(p.preferred_name || p.first_name || (p.email ?? "").split("@")[0] || p.id);
  const full = (p: Person) => slugify([p.preferred_name || p.first_name, p.last_name].filter(Boolean).join(" ")) || first(p);

  const counts = new Map<string, number>();
  for (const p of people) counts.set(first(p), (counts.get(first(p)) ?? 0) + 1);
  const proposed = people.map(p => [p, counts.get(first(p))! > 1 ? full(p) : first(p)] as const);

  const fullCounts = new Map<string, number>();
  for (const [, s] of proposed) fullCounts.set(s, (fullCounts.get(s) ?? 0) + 1);

  const out = new Map<string, string>();
  for (const [p, s] of proposed) out.set(p.id, fullCounts.get(s)! > 1 ? `${s}-${p.id.slice(0, 4)}` : s);
  return out;
}

export function labelSlugs(labels: Label[]): Map<string, string> {
  const out = new Map<string, string>();
  const seen = new Set<string>();
  for (const l of labels) {
    let s = slugify(l.name) || l.id.slice(0, 8);
    if (seen.has(s)) s = `${s}-${l.id.slice(0, 4)}`;
    seen.add(s);
    out.set(l.id, s);
  }
  return out;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function resolve(values: string[], slugs: Map<string, string>): string[] {
  const byslug = new Map<string, string>();
  for (const [id, s] of slugs) byslug.set(s, id);
  const out: string[] = [];
  for (const v of values) {
    const key = v.trim().toLowerCase();
    const id = byslug.get(key) ?? (UUID.test(key) && slugs.has(key) ? key : undefined);
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

export function parseFilters(
  params: URLSearchParams,
  ctx: { personSlugs: Map<string, string>; labelSlugs: Map<string, string> },
): FilterState {
  const sort = params.get("sort") as SortKey | null;
  return {
    people: resolve(params.getAll("person"), ctx.personSlugs),
    labels: resolve(params.getAll("label"), ctx.labelSlugs),
    q: params.get("q") ?? "",
    sort: sort && SORT_KEYS.includes(sort) ? sort : "manual",
    compare: params.get("compare") === "1",
    archived: params.get("archived") === "1",
  };
}

/**
 * Writes the filters into `params`, leaving every other parameter
 * (?card=…, ?gmail=…) alone. Returns a new object.
 */
export function writeFilters(
  params: URLSearchParams,
  f: FilterState,
  ctx: { personSlugs: Map<string, string>; labelSlugs: Map<string, string> },
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const k of ["person", "label", "q", "sort", "compare", "archived"]) next.delete(k);
  for (const id of f.people) { const s = ctx.personSlugs.get(id); if (s) next.append("person", s); }
  for (const id of f.labels) { const s = ctx.labelSlugs.get(id); if (s) next.append("label", s); }
  if (f.q.trim()) next.set("q", f.q);
  if (f.sort !== "manual") next.set("sort", f.sort);
  if (f.compare) next.set("compare", "1");
  if (f.archived) next.set("archived", "1");
  return next;
}

/** "1042", "#1042", "ss-1042" and "SS 1042" all name card 1042. */
export function parseCardQuery(q: string): { prefix: string | null; number: number } | null {
  const m = /^(?:([a-z]+)[\s-]?)?#?(\d+)$/i.exec(q.trim());
  if (!m) return null;
  return { prefix: m[1] ? m[1].toLowerCase() : null, number: Number(m[2]) };
}

// ── Applying them ──────────────────────────────────────────────────────────

export interface CardIndex {
  assigneesByCard: Map<string, string[]>;
  labelsByCard: Map<string, string[]>;
  keyPrefix: string | null;
}

/** "1042", "#1042", "ss-1042" and "SS 1042" all find card 1042. */
function matchesNumber(q: string, card: Card, prefix: string | null): boolean {
  const parsed = parseCardQuery(q);
  if (!parsed) return false;
  if (parsed.prefix && (!prefix || parsed.prefix !== prefix.toLowerCase())) return false;
  return parsed.number === card.number;
}

/**
 * What the columns are allowed to show.
 *
 * The live board hides archived cards. The archive shows only those.
 * A search from the live board includes archived cards that match, so
 * typing a title or an SS-number finds a card that is off the board.
 */
export function applyArchiveView(cards: Card[], f: Pick<FilterState, "archived" | "q">): Card[] {
  if (f.archived) return cards.filter(isArchived);
  if (f.q.trim()) return cards;
  return cards.filter(c => !isArchived(c));
}

/**
 * A person's cards are the ones assigned to them. A card nobody is assigned
 * to counts as its creator's, so new drafts don't vanish from their author's
 * view.
 */
export function cardBelongsTo(card: Card, personIds: string[], idx: CardIndex): boolean {
  const assigned = idx.assigneesByCard.get(card.id) ?? [];
  if (assigned.length > 0) return assigned.some(a => personIds.includes(a));
  return !!card.created_by && personIds.includes(card.created_by);
}

export function applyFilters(cards: Card[], f: FilterState, idx: CardIndex): Card[] {
  const q = f.q.trim().toLowerCase();
  return cards.filter(c => {
    if (f.people.length && !cardBelongsTo(c, f.people, idx)) return false;
    if (f.labels.length) {
      const ls = idx.labelsByCard.get(c.id) ?? [];
      if (!f.labels.some(l => ls.includes(l))) return false;
    }
    if (q && !c.title.toLowerCase().includes(q) && !matchesNumber(q, c, idx.keyPrefix)) return false;
    return true;
  });
}

const PRIORITY_RANK: Record<Priority, number> = { high: 0, med: 1, low: 2 };

/** Sorts one column's cards. "manual" is the drag order. */
export function sortCards(cards: Card[], sort: SortKey): Card[] {
  const out = [...cards];
  const byPos = (a: Card, b: Card) => Number(a.position) - Number(b.position);
  switch (sort) {
    case "updated":
      return out.sort((a, b) => b.updated_at.localeCompare(a.updated_at) || byPos(a, b));
    case "created":
      return out.sort((a, b) => b.created_at.localeCompare(a.created_at) || byPos(a, b));
    case "due":
      return out.sort((a, b) => {
        if (a.due_date === b.due_date) return byPos(a, b);
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date.localeCompare(b.due_date);
      });
    case "priority":
      return out.sort((a, b) => (PRIORITY_RANK[a.priority] ?? 1) - (PRIORITY_RANK[b.priority] ?? 1) || byPos(a, b));
    default:
      return out.sort(byPos);
  }
}
